import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Layers } from 'lucide-react';
import { TOP_ITEMS, HUBS, getHubItems, SEARCH_TYPE_ICONS } from './hubs';

/**
 * Top-bar global search with autocomplete. Combines instant client-side
 * nav-item matching with debounced remote content search via
 * /api/admin/search. Cmd/Ctrl-K opens it from anywhere. Extracted from
 * AdminLayout.jsx during the iter175 layout refactor.
 */
export function TopbarSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  // Global Cmd-K / Ctrl-K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Client-side nav index
  const allNavItems = [...TOP_ITEMS, ...HUBS.flatMap((h) => getHubItems(h).map((i) => ({ ...i, hub: h.label })))];

  // Debounced remote search
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      // Nav-item matches (instant, client-side)
      const needle = q.toLowerCase();
      const navHits = allNavItems
        .filter((it) => it.label.toLowerCase().includes(needle))
        .slice(0, 5)
        .map((it) => ({
          type: 'nav', title: it.label, subtitle: it.hub || 'Admin',
          href: it.href,
        }));

      // Remote content
      const API = process.env.REACT_APP_BACKEND_URL || '';
      let remoteHits = [];
      try {
        const r = await fetch(`${API}/api/admin/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          remoteHits = d.items || [];
        }
      } catch { /* offline-tolerant */ }

      setItems([...navHits, ...remoteHits]);
      setFocusIdx(0);
      setLoading(false);
    }, 180);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, token]);

  const pick = (item) => {
    if (!item) return;
    setOpen(false); setQ('');
    if (item.external) { window.open(item.href, '_blank'); return; }
    navigate(item.href);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(items[focusIdx]); }
  };

  return (
    <div className="flex-1 max-w-xl mx-4 hidden md:block" style={{ position: 'relative' }}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          onKeyDown={onKey}
          placeholder="Search people, blogs, case studies, nav..."
          data-testid="topbar-search"
          className="w-full py-2 pr-16 text-sm rounded-md focus:outline-none"
          style={{
            paddingLeft: '36px',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#fff',
            fontSize: '13px',
          }}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <kbd style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.15)', fontFamily: 'system-ui' }}>{'\u2318'}</kbd>
          <kbd style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.15)', fontFamily: 'system-ui' }}>K</kbd>
        </div>
      </div>

      {open && q.trim().length >= 2 && (
        <div data-testid="topbar-search-dropdown"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            marginTop: '6px', background: 'var(--hs-card)',
            border: '1px solid var(--hs-border)', borderRadius: '10px',
            boxShadow: '0 14px 40px rgba(15,23,42,0.25)',
            padding: '6px', maxHeight: '420px', overflowY: 'auto',
            zIndex: 100,
          }}>
          {loading && items.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--hs-text-faint)', fontSize: '12px' }}>
              <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> Searching...
            </div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--hs-text-faint)', fontSize: '12px' }}>
              No results for "{q}". Try a person name, blog title, or case study.
            </div>
          )}
          {items.map((it, i) => {
            const ItemIcon = SEARCH_TYPE_ICONS[it.type] || Layers;
            const isActive = i === focusIdx;
            return (
              <button key={`${it.type}-${it.href}-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                data-testid={`topbar-search-item-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', borderRadius: '7px', width: '100%',
                  background: isActive ? 'var(--hs-hover)' : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'var(--hs-bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ItemIcon size={13} style={{ color: 'var(--hs-accent)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--hs-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</div>
                  {it.subtitle && <div style={{ fontSize: '11px', color: 'var(--hs-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.subtitle}</div>}
                </div>
                <span style={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hs-text-faint)', padding: '2px 6px', borderRadius: '4px', background: 'var(--hs-bg-muted)', flexShrink: 0 }}>{it.type.replace('_', ' ')}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
